import { taskTags } from '@shared/task-tags';
import type { TaskCard, TaskColumn, TaskContextRef } from '@shared/types/tasks';
import { rankBetween } from '@shared/task-rank';
import { createTask } from '@/lib/tasks-api';
import { useAuthStore } from '@/stores/authStore';
import { lastRankIn } from '@/stores/taskStore';
import { optimisticCreate } from '@/stores/taskOptimistic';
import { parseMentions } from '@shared/task-contexts';
import { displayToMarkup, mentionAdditions, type PickedMention } from './mention-text';

export type DraftContext = TaskContextRef & { title: string; url: string; parent?: TaskContextRef | null };

/** A one-line title with @ mentions: the title reads "Tell @Sam to…"; Sam is assigned, things are linked. */
export interface QuickTask {
  title: string;
  assignee_ids: string[];
  contexts: DraftContext[];
}

export function quickTaskFromText(text: string, mentions: PickedMention[]): QuickTask {
  // Only mentions still in the text count (one may have been typed over).
  const parsed = parseMentions(displayToMarkup(text, mentions));
  return {
    title: text.replace(/\s+/g, ' ').trim(),
    assignee_ids: parsed.userIds,
    contexts: parsed.contexts.map((c) => ({ type: c.type, id: c.id, title: c.title, url: '' })),
  };
}

/**
 * Create a task instantly: the id is chosen here, the card shows on the Board and in panels,
 * and the task can be opened (and chatted on) straight away. `saved` settles when the server
 * has it; if that fails the card disappears again and a toast says why.
 */
export function createTaskNow(input: {
  title: string;
  column: TaskColumn;
  description?: string;
  assignee_ids?: string[];
  label_ids?: string[];
  contexts?: DraftContext[];
  /** A type picked in the dialog; left out, Jev may suggest one. */
  type_id?: string;
  /** The Board's default type, shown until the server answers. */
  defaultTypeId?: string;
}): { id: string; saved: Promise<boolean> } {
  const id = crypto.randomUUID();
  const now = new Date().toISOString();
  const me = useAuthStore.getState().user?.id ?? null;
  const contexts = input.contexts ?? [];
  const linked = contexts.map((c) => ({ type: c.type, id: c.id, title: c.title, url: c.url, parent: c.parent ?? null, source: 'page' as const }));
  const card: TaskCard = {
    id,
    // The server hands out the Task number; none shows until it answers.
    number: 0,
    title: input.title,
    column_id: input.column.id,
    rank: rankBetween(lastRankIn(input.column.id), null),
    assignee_ids: input.assignee_ids ?? [],
    label_ids: input.label_ids ?? [],
    // Left out, the default type.
    type_id: input.type_id ?? input.defaultTypeId ?? '',
    size: null,
    kind: null,
    priority: null,
    // Jev's picks and the summary arrive over the live socket once the project's hub has made them (T-17).
    ai_suggested: [],
    ai_summary: null,
    ai_summary_due_at: null,
    prs: [],
    stacked_on: null,
    ai_machine: null,
    parent: null,
    show_on_board: false,
    subtasks_one_at_a_time: false,
    subtasks_defer_questions: false,
    in_chain: false,
    chain_break: false,
    chain_break_passed_at: null,
    subtasks_chain_stopped_for: null,
    subtasks_concurrency: 1,
    after: [],
    draft: false,
    subtasks: null,
    subtask_waiting: [],
    subtask_titles: [],
    waiting_on: [],
    cover_attachment_id: null,
    // Creating a task follows it, as on the server.
    follower_ids: me ? [me] : [],
    // The server collects the same #tags from the title and description.
    tags: taskTags([input.title, input.description]),
    // Things @'d in the description are linked by the server too.
    contexts: mentionAdditions('', input.description ?? '', { assignee_ids: [], contexts: linked }, []).contexts ?? linked,
    chat_count: 0,
    attachment_count: 0,
    pending_patches: 0,
    pending_approvals: 0,
    failed_run: null,
    created_by_user_id: me,
    requested_by_user_id: me,
    archived: false,
    done_at: input.column.is_done ? now : null,
    snoozed_until: null,
    repeat: null,
    repeat_next_at: null,
    upstream: null,
    created_at: now,
    updated_at: now,
  };
  const saved = optimisticCreate(
    card,
    { description: input.description ?? '', following: true, follower_ids: me ? [me] : [] },
    createTask({
      id,
      title: input.title,
      description: input.description,
      column_id: input.column.id,
      assignee_ids: input.assignee_ids,
      label_ids: input.label_ids,
      contexts: contexts.map((c) => ({ type: c.type, id: c.id, title: c.title })),
      type_id: input.type_id,
    })
  );
  return { id, saved };
}
